import {Phone, Calendar, Shield, Sparkles, Award, Car, Check, Zap} from 'lucide-react'
import { useNavigate } from "react-router-dom"
import CanonicalTag from '@/components/CanonicalTag'

const RacingStripesGraphicsPage = () => {
  const navigate = useNavigate()

  return (
    <>
      <CanonicalTag url="https://autowrapsandiego.com/racing-stripes-graphics" />
      <div className="pt-20 min-h-screen bg-black">
      {/* Hero */}
      <section className="bg-gradient-to-br from-black via-gray-900 to-black text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-5xl font-bold mb-6">Racing Stripes & Graphics in San Diego</h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-4">
              Professional racing stripes and custom vehicle graphics installation in San Diego. Transform your performance or muscle car with bold, precision-applied racing stripes that capture the aggressive spirit of motorsports.
            </p>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-8">
              Serving North County San Diego including Vista, Carlsbad, and Oceanside, we specialize in classic dual stripes, modern offset designs, custom graphics packages, and competition-inspired livery for performance vehicles.
            </p>
            <div className="flex gap-4 justify-center">
              <a href="tel:858-282-8119" className="flex items-center gap-2 bg-cyan-500 hover:bg-cyan-600 text-white px-6 py-3 rounded-lg font-semibold transition">
                <Phone size={18} />
                858-282-8119
              </a>
              <button onClick={() => { navigate("/contact"); setTimeout(() => window.scrollTo(0, 0), 100); }} className="flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white px-6 py-3 rounded-lg font-semibold transition">
                <Calendar size={18} />
                Book Consultation
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Stripe & Graphics Services */}
      <section className="py-20 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold mb-12 text-white text-center">Racing Stripes & Graphics Services</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="bg-gray-900 p-8 rounded-xl border border-cyan-500/20">
              <h3 className="text-2xl font-bold mb-4 text-white">Classic Racing Stripes</h3>
              <p className="text-gray-300">Timeless dual stripes running full-length over hood, roof, and trunk. Available in any width and color combination.</p>
            </div>
            <div className="bg-gray-900 p-8 rounded-xl border border-cyan-500/20">
              <h3 className="text-2xl font-bold mb-4 text-white">Hood Stripes</h3>
              <p className="text-gray-300">Aggressive hood-only stripe packages from center dual stripes to wide offset designs and custom patterns.</p>
            </div>
            <div className="bg-gray-900 p-8 rounded-xl border border-cyan-500/20">
              <h3 className="text-2xl font-bold mb-4 text-white">Side Graphics & Decals</h3>
              <p className="text-gray-300">Rocker panel graphics, side stripes, door accent lines, and custom vinyl decals for unique styling.</p>
            </div>
            <div className="bg-gray-900 p-8 rounded-xl border border-cyan-500/20">
              <h3 className="text-2xl font-bold mb-4 text-white">Custom Competition Livery</h3>
              <p className="text-gray-300">Full race-inspired graphics packages with numbers, sponsors, and track-ready designs for serious enthusiasts.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Popular Stripe Styles */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold mb-12 text-white text-center">Popular Stripe Styles</h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-black p-8 rounded-xl border border-cyan-500/20">
              <div className="bg-cyan-500/20 w-16 h-16 rounded-full flex items-center justify-center mb-6 border border-cyan-500">
                <Zap className="text-cyan-400" size={32} />
              </div>
              <h3 className="text-2xl font-bold mb-3 text-white">Rally Stripes</h3>
              <p className="text-gray-300">Dual center stripes running the full length of the vehicle, inspired by classic racing heritage. Perfect for Mustangs, Camaros, Challengers.</p>
            </div>
            <div className="bg-black p-8 rounded-xl border border-cyan-500/20">
              <div className="bg-cyan-500/20 w-16 h-16 rounded-full flex items-center justify-center mb-6 border border-cyan-500">
                <Sparkles className="text-cyan-400" size={32} />
              </div>
              <h3 className="text-2xl font-bold mb-3 text-white">Offset Racing Stripes</h3>
              <p className="text-gray-300">Modern asymmetric stripes positioned off-center for aggressive, contemporary styling popular on performance imports and sports cars.</p>
            </div>
            <div className="bg-black p-8 rounded-xl border border-cyan-500/20">
              <div className="bg-cyan-500/20 w-16 h-16 rounded-full flex items-center justify-center mb-6 border border-cyan-500">
                <Car className="text-cyan-400" size={32} />
              </div>
              <h3 className="text-2xl font-bold mb-3 text-white">GT Stripes</h3>
              <p className="text-gray-300">Wide single center stripe or bold double stripes with contrasting pin stripes, evoking classic GT40 and muscle car racing pedigree.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold mb-12 text-white text-center">Why Choose Mr Wrap for Racing Stripes</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="bg-gray-900 p-8 rounded-xl border border-cyan-500/20">
              <div className="bg-cyan-500/20 w-16 h-16 rounded-full flex items-center justify-center mb-6 border border-cyan-500">
                <Award className="text-cyan-400" size={32} />
              </div>
              <h3 className="text-2xl font-bold mb-3 text-white">Perfect Alignment</h3>
              <p className="text-gray-300">Laser-measured centerlines and professional alignment tools ensure stripes run perfectly straight and symmetrical.</p>
            </div>
            <div className="bg-gray-900 p-8 rounded-xl border border-cyan-500/20">
              <div className="bg-cyan-500/20 w-16 h-16 rounded-full flex items-center justify-center mb-6 border border-cyan-500">
                <Sparkles className="text-cyan-400" size={32} />
              </div>
              <h3 className="text-2xl font-bold mb-3 text-white">Premium Vinyl Films</h3>
              <p className="text-gray-300">3M and Avery high-performance vinyl in hundreds of colors including gloss, matte, metallic, and specialty finishes.</p>
            </div>
            <div className="bg-gray-900 p-8 rounded-xl border border-cyan-500/20">
              <div className="bg-cyan-500/20 w-16 h-16 rounded-full flex items-center justify-center mb-6 border border-cyan-500">
                <Check className="text-cyan-400" size={32} />
              </div>
              <h3 className="text-2xl font-bold mb-3 text-white">Clean Edges & Contours</h3>
              <p className="text-gray-300">Expert application around body lines, curves, and panel transitions for factory-quality appearance.</p>
            </div>
            <div className="bg-gray-900 p-8 rounded-xl border border-cyan-500/20">
              <div className="bg-cyan-500/20 w-16 h-16 rounded-full flex items-center justify-center mb-6 border border-cyan-500">
                <Shield className="text-cyan-400" size={32} />
              </div>
              <h3 className="text-2xl font-bold mb-3 text-white">Custom Design Service</h3>
              <p className="text-gray-300">Work with our designers to create unique stripe patterns, width combinations, and graphic elements tailored to your vision.</p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold mb-12 text-white text-center">Racing Stripes & Graphics FAQs</h2>
          <div className="space-y-6">
            <div className="bg-black p-6 rounded-xl border border-cyan-500/20">
              <h3 className="text-xl font-bold mb-3 text-white">How long do racing stripes last?</h3>
              <p className="text-gray-300">Premium vinyl racing stripes last 5-7 years with proper care. High-quality 3M and Avery films resist fading, cracking, and peeling. Indoor storage and regular maintenance extend lifespan even further.</p>
            </div>
            <div className="bg-black p-6 rounded-xl border border-cyan-500/20">
              <h3 className="text-xl font-bold mb-3 text-white">Can racing stripes be removed without damaging paint?</h3>
              <p className="text-gray-300">Yes. Professional vinyl stripes remove cleanly when done by experts. Quality vinyl leaves no residue or paint damage, making stripes perfect for leased vehicles or if you want to change designs later.</p>
            </div>
            <div className="bg-black p-6 rounded-xl border border-cyan-500/20">
              <h3 className="text-xl font-bold mb-3 text-white">What stripe width should I choose?</h3>
              <p className="text-gray-300">Depends on your vehicle and style preference. Classic Mustang/Camaro stripes are typically 8-10 inches. Modern performance cars often use 12-14 inch stripes. We'll recommend proportions that complement your vehicle's size and body lines.</p>
            </div>
            <div className="bg-black p-6 rounded-xl border border-cyan-500/20">
              <h3 className="text-xl font-bold mb-3 text-white">Can you match factory racing stripe colors?</h3>
              <p className="text-gray-300">Absolutely. We can match OEM stripe colors for brands like Ford, Dodge, Chevy, and others. Bring a color sample or vehicle build sheet and we'll match it precisely.</p>
            </div>
            <div className="bg-black p-6 rounded-xl border border-cyan-500/20">
              <h3 className="text-xl font-bold mb-3 text-white">How long does stripe installation take?</h3>
              <p className="text-gray-300">Full-length racing stripes typically take 4-6 hours for proper measuring, alignment, and application. Hood-only stripes take 2-3 hours. Complex custom graphics may require a full day. We never rush stripe installations.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-gradient-to-r from-cyan-500 to-cyan-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-6 text-white">Ready for Racing Stripes?</h2>
          <p className="text-xl text-white/90 mb-8">
            Schedule your free racing stripes consultation and design quote
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button onClick={() => { navigate("/contact"); setTimeout(() => window.scrollTo(0, 0), 100); }} className="bg-orange-500 hover:bg-orange-600 text-white font-bold py-4 px-12 rounded-lg transition-all transform hover:scale-105 shadow-xl inline-flex items-center justify-center gap-2">
              <Calendar size={20} />
              Book Consultation
            </button>
            <a href="tel:858-282-8119" className="bg-white hover:bg-gray-100 text-gray-900 font-bold py-4 px-12 rounded-lg transition-all transform hover:scale-105 shadow-xl inline-flex items-center justify-center gap-2">
              <Phone size={20} />
              Call: 858-282-8119
            </a>
          </div>
        </div>
      </section>
      </div>
    </>
  )
}

export default RacingStripesGraphicsPage
